import React from 'react';
import type { RemoteCursor } from '@/collaboration/yjs-provider';

interface RemoteCursorsProps {
  cursors: RemoteCursor[];
}

/**
 * Renders other users' cursors as colored pointer arrows with a name label.
 * This component should be rendered as a fixed overlay on top of the canvas.
 */
const RemoteCursors: React.FC<RemoteCursorsProps> = ({ cursors }) => {
  if (cursors.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9999,
        overflow: 'hidden',
      }}
    >
      {cursors.map((cursor) => (
        <CursorPointer key={cursor.clientId} cursor={cursor} />
      ))}
    </div>
  );
};

interface CursorPointerProps {
  cursor: RemoteCursor;
}

const CursorPointer: React.FC<CursorPointerProps> = ({ cursor }) => {
  const { user, cursor: pos } = cursor;
  const color = user.color || '#3B82F6';
  const displayName = `${user.firstName} ${user.lastName}`.trim() || 'Anonymous';

  return (
    <div
      style={{
        position: 'absolute',
        left: pos.x,
        top: pos.y,
        transition: 'left 0.05s linear, top 0.05s linear',
        pointerEvents: 'none',
      }}
    >
      {/* Arrow pointer SVG */}
      <svg
        width="20"
        height="24"
        viewBox="0 0 20 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block' }}
      >
        <path
          d="M1 1L1 19L6.5 14L12 21L15 19.5L9.5 12.5L17 11L1 1Z"
          fill={color}
          stroke="white"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
      </svg>

      {/* Name label */}
      <div
        style={{
          position: 'absolute',
          left: 16,
          top: 16,
          background: color,
          color: '#FFFFFF',
          fontSize: '11px',
          lineHeight: '16px',
          fontWeight: 500,
          padding: '2px 6px',
          borderRadius: '4px',
          whiteSpace: 'nowrap',
          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          userSelect: 'none',
        }}
      >
        {displayName}
      </div>
    </div>
  );
};

export default RemoteCursors;
